<template>
	<div class="container-fluid mt-2">
		<PageHeader
			:data="{
				name: 'Brands',
				breadcrumbs: [{ name: 'Home', to: '/' }, { name: 'Marketing Tools', to: '/marketing/brands' }],
			}"
		/>

		<div v-if="errorMessage" class="alert alert-warning py-2 small">{{ errorMessage }}</div>

		<!-- Connection help -->
		<div class="card mb-4">
			<div class="card-header fw-bold py-2">How brands connect</div>
			<div class="card-body small">
				<p class="mb-2">
					UTM and campaign data is read from each brand's Google Analytics 4 property through one shared Google service account.
					There is no per-brand API key. To connect a brand:
				</p>
				<ol class="ps-3 mb-2">
					<li class="mb-1">Enter the brand's numeric GA4 property ID below (find it in GA Admin, Property settings, Property details).</li>
					<li class="mb-1">In GA Admin, Property Access Management, grant <strong>Viewer</strong> (or Analyst) to this service account:</li>
				</ol>
				<div class="d-flex align-items-center mb-2">
					<code class="bg-light border rounded p-2 me-2 flex-grow-1">{{ serviceAccount }}</code>
					<button class="btn btn-sm btn-outline-secondary text-nowrap" @click="copy(serviceAccount, 'sa')">{{ copiedKey === 'sa' ? 'Copied' : 'Copy' }}</button>
				</div>
				<p class="mb-0 text-muted">The Chris Willow site key is generated automatically per brand and is used by the tracker snippet.</p>
			</div>
		</div>

		<!-- Add brand -->
		<div class="card mb-4">
			<div class="card-header fw-bold py-2">Add a brand</div>
			<div class="card-body">
				<div class="row g-2 align-items-end">
					<div class="col-md-4">
						<label class="form-label mb-0 small text-muted">Brand name</label>
						<input class="form-control form-control-sm" v-model="newBrand.name" placeholder="ClerkMinutes" />
					</div>
					<div class="col-md-3">
						<label class="form-label mb-0 small text-muted">Domain (optional)</label>
						<input class="form-control form-control-sm" v-model="newBrand.domain" placeholder="clerkminutes.com" />
					</div>
					<div class="col-md-3">
						<label class="form-label mb-0 small text-muted">GA4 property ID (optional)</label>
						<input class="form-control form-control-sm" v-model="newBrand.ga4_property_id" placeholder="e.g. 312345678" />
					</div>
					<div class="col-md-2">
						<button class="btn btn-sm btn-primary w-100" :disabled="!newBrand.name.trim() || saving" @click="addBrand">Add brand</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Brands list -->
		<div class="card mb-5">
			<div class="card-header fw-bold py-2 d-flex justify-content-between align-items-center">
				<span>Brands</span>
				<span v-if="loading" class="spinner-border spinner-border-sm" role="status"></span>
			</div>
			<div class="table-responsive">
				<table class="table table-sm align-middle mb-0">
					<thead>
						<tr>
							<th style="min-width: 150px">Name</th>
							<th style="min-width: 140px" title="Used to validate tracker beacons for this brand">Domain</th>
							<th style="min-width: 140px">GA4 property ID</th>
							<th>Chris Willow site key</th>
							<th>Tracker</th>
							<th class="text-end" style="width: 150px">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="brand in brands" :key="brand.id">
							<td><input class="form-control form-control-sm" v-model="brand.name" /></td>
							<td><input class="form-control form-control-sm" v-model="brand.domain" placeholder="not set" /></td>
							<td><input class="form-control form-control-sm" v-model="brand.ga4_property_id" placeholder="not connected" /></td>
							<td>
								<div class="d-flex align-items-center">
									<code class="small text-truncate me-2" style="max-width: 180px">{{ brand.cw_site_key || '-' }}</code>
									<button v-if="brand.cw_site_key" class="btn btn-sm btn-outline-secondary py-0" @click="copy(brand.cw_site_key, 'key' + brand.id)">{{ copiedKey === 'key' + brand.id ? 'Copied' : 'Copy' }}</button>
								</div>
							</td>
							<td><span class="badge" :class="brand.cw_status === 'installed' ? 'bg-success' : 'bg-secondary'">{{ brand.cw_status === 'installed' ? 'Installed' : 'Not installed' }}</span></td>
							<td class="text-end text-nowrap">
								<button class="btn btn-sm btn-primary py-0 me-1" :disabled="saving" @click="saveBrand(brand)">Save</button>
								<button class="btn btn-sm btn-outline-danger py-0" :disabled="saving" @click="archiveBrand(brand)">Archive</button>
							</td>
						</tr>
						<tr v-if="!brands.length && !loading">
							<td colspan="6" class="text-center text-muted py-3">No brands yet. Add one above.</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import restApi from '../../api'
import PageHeader from '@/components/layout/PageHeader.vue'

export default {
	name: 'MarketingBrands',
	components: { PageHeader },
	data() {
		return {
			loading: false,
			saving: false,
			brands: [],
			newBrand: { name: '', ga4_property_id: '', domain: '' },
			serviceAccount: 'client-dashboard-service-accou@client-dashboard-analytics.iam.gserviceaccount.com',
			copiedKey: '',
			errorMessage: '',
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
	},
	created() {
		this.loadBrands()
	},
	methods: {
		loadBrands() {
			this.loading = true
			this.errorMessage = ''
			restApi
				.get('/marketing/brands')
				.then(({ data }) => {
					this.brands = Array.isArray(data) ? data : []
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					this.errorMessage =
						'Could not load brands. If this is the first run, apply the database migration (php artisan migrate) to create the brands table.'
					console.log(error)
				})
		},
		addBrand() {
			if (!this.newBrand.name.trim()) return
			this.saving = true
			restApi
				.post('/marketing/brands', { name: this.newBrand.name.trim(), ga4_property_id: this.newBrand.ga4_property_id })
				.then(({ data }) => {
					this.brands.push(data)
					this.newBrand = { name: '', ga4_property_id: '' }
					this.saving = false
				})
				.catch(error => {
					this.saving = false
					this.errorMessage = 'Could not add brand.'
					console.log(error)
				})
		},
		saveBrand(brand) {
			this.saving = true
			restApi
				.put(`/marketing/brands/${brand.id}`, { name: brand.name, ga4_property_id: brand.ga4_property_id })
				.then(({ data }) => {
					const idx = this.brands.findIndex(b => b.id === data.id)
					if (idx !== -1) this.$set(this.brands, idx, data)
					this.saving = false
				})
				.catch(error => {
					this.saving = false
					this.errorMessage = 'Could not save brand.'
					console.log(error)
				})
		},
		archiveBrand(brand) {
			if (!window.confirm(`Archive ${brand.name}? It will be hidden from the brand pickers.`)) return
			this.saving = true
			restApi
				.delete(`/marketing/brands/${brand.id}`)
				.then(() => {
					this.brands = this.brands.filter(b => b.id !== brand.id)
					this.saving = false
				})
				.catch(error => {
					this.saving = false
					this.errorMessage = 'Could not archive brand.'
					console.log(error)
				})
		},
		copy(text, key) {
			navigator.clipboard.writeText(text).then(() => {
				this.copiedKey = key
				setTimeout(() => (this.copiedKey = ''), 1500)
			})
		},
	},
}
</script>
